// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * A resource view: its image cropped to its region (`cropBackground`), a
 * panorama wrapping across its seam. Measures its own box and probes the
 * image once for its aspect; a failed load shows "Intet billede" — or, with
 * `quietFallback` (the table's ~32×24 thumbnail cell, too small for that
 * text), the same empty placeholder as "no view".
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { ResourceView } from '../../api/types';
import { cropBackground, viewFetchSize, viewImageUrl } from '../../kortlaegning/view';
import styles from './ViewImage.module.css';

export interface ViewImageProps {
  view: ResourceView;
  /** Longest edge it is drawn at (px), for the fetch size. */
  displaySize: number;
  /** Fetch cap (a table thumbnail keeps it small). */
  maxFetch?: number;
  className?: string;
  /** Accessible name; `null` for a decorative thumbnail. */
  label: string | null;
  onError?: () => void;
  /** A failed load renders as the same quiet empty placeholder as "no view" (no text, no `.fallback` styling) — for a cell too small for "Intet billede". */
  quietFallback?: boolean;
  /** `cover`: the region fills the box, trimmed to it (an edge-to-edge image); default `contain` shows it whole. */
  fit?: 'contain' | 'cover';
}

export function ViewImage({
  view,
  displaySize,
  maxFetch = 2048,
  className,
  label,
  onError,
  quietFallback,
  fit = 'contain',
}: ViewImageProps) {
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [aspect, setAspect] = useState(0);
  const [failed, setFailed] = useState(false);
  const url = viewImageUrl(view, viewFetchSize(view, displaySize, maxFetch));

  // A callback ref (not an effect keyed on mount) so a failed→new-url
  // remount — the element is torn down (the fallback <span> instead) and
  // rebuilt — re-observes the *new* node; an effect with a [] dependency
  // array would only ever attach to the first one.
  const observerRef = useRef<ResizeObserver | null>(null);
  const setRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    const measure = () => setBox({ width: el.clientWidth, height: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    observerRef.current = observer;
  }, []);

  useEffect(() => {
    let live = true;
    setFailed(false);
    setAspect(0);
    const probe = new Image();
    probe.onload = () => {
      if (live && probe.naturalHeight > 0) setAspect(probe.naturalWidth / probe.naturalHeight);
    };
    probe.onerror = () => {
      if (!live) return;
      setFailed(true);
      onError?.();
    };
    probe.src = url;
    return () => {
      live = false;
    };
    // onError is a callback prop; re-probing on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const classes = [styles.view, className].filter(Boolean).join(' ');
  if (failed) {
    if (quietFallback) {
      return <span className={className} aria-hidden="true" />;
    }
    return (
      <span className={[styles.fallback, className].filter(Boolean).join(' ')} aria-hidden={label === null || undefined}>
        Intet billede
      </span>
    );
  }
  const crop = cropBackground(view.region, aspect, box, view.kind === 'panorama', fit);
  return (
    <div
      ref={setRef}
      className={classes}
      role={label === null ? undefined : 'img'}
      aria-label={label ?? undefined}
      aria-hidden={label === null || undefined}
      style={aspect > 0 ? { backgroundImage: `url("${url}")`, ...crop } : undefined}
    />
  );
}
